import React from "react";
import { gql } from "apollo-boost";
import { useQuery } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";
import { Loading } from "components/common";
import { TrueLinkCreditReportType } from "types";
import { convertToReadableAddress } from "./helpers";
import { CreditReportAccounts } from "./components/Accounts";
import { CreditReportPublicRecord } from "./components/PublicRecord";
import {
  CreditReportError,
  CreditReportInquiries,
  PersonalInformation,
} from "./components";

const USER_CREDIT_REPORT_QUERY = gql`
  query CreditReport {
    CreditReport {
      status
      rawTUCreditReport {
        score
        rawReport
      }
    }
  }
`;

export const CreditReport = () => {
  const { data, loading, error } = useQuery(USER_CREDIT_REPORT_QUERY);
  const history = useHistory();
  const errorMessage = error?.graphQLErrors?.[0]?.message || "";
  const creditReportStr = data?.CreditReport?.rawTUCreditReport?.rawReport;
  const creditReport = creditReportStr
    ? (JSON.parse(creditReportStr)
      ?.TrueLinkCreditReportType as TrueLinkCreditReportType)
    : null;
  const accounts = creditReport?.TradeLinePartition || [];
  const borrowerAddress =
    creditReport?.Borrower[0]?.BorrowerAddress[0]?.CreditAddress[0]?.$;
  const address = borrowerAddress
    ? convertToReadableAddress(borrowerAddress)
    : "";

  const publicRecords = creditReport?.PulblicRecordPartition || [];
  const inquiries = creditReport?.InquiryPartition || [];

  const nameObj = creditReport?.Borrower?.[0]?.BorrowerName?.[0]?.Name?.[0]?.$;
  const borrower = creditReport?.Borrower?.[0];

  const personalInformationProps = {
    name: [
      `${nameObj?.first || ""} ${nameObj?.middle || ""} ${
        nameObj?.last || ""
      }`.trim(),
    ],
    alsoKnownAs: ["N/A"], // TODO - get this from the credit report
    dateOfBirth: borrower?.Birth?.[0]?.$?.date || "N/A",
    socialSecurityNumber:
      borrower?.SocialPartition?.[0]?.Social?.[0]?.SocialSecurityNumber?.[0] ||
      "N/A",
    addressesReported: [address],
    employers: [borrower?.Employer?.[0]?.$?.name || "N/A"],
  };

  const reportDate = creditReport?.Sources?.[0]?.InquiryDate?.[0]?.[0];

  return (
    <div className="h-full bg-neutral-50 pt-10 px-16 md:px-32 pb-20 md:pb-40">
      <button
        type="button"
        onClick={history.goBack}
        className="bg-transparent hover:bg-neutral-200 active:bg-neutral-300 text-black py-2 rounded-md focus:outline-none text-center font-bold"
      >
        Back
      </button>
      {loading && <Loading showBackdrop={undefined} />}
      <div className="mt-5 h-full">
        <p className="text-4xl font-bold">Credit report</p>
        {reportDate && (
          <p className="mt-1">
            The report was generated on{" "}
            <span className="font-bold">{reportDate}</span>. Reports are
            refreshed on a monthly basis.
          </p>
        )}
        {!loading && !creditReport && (
          <CreditReportError className="mt-8" errorMessage={errorMessage} />
        )}
        {creditReport && (
          <>
            <PersonalInformation
              className="mt-8"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...personalInformationProps}
            />
            {accounts.length > 0 && (
              <CreditReportAccounts className="mt-8" accounts={accounts} />
            )}
            {publicRecords.length > 0 && (
              <CreditReportPublicRecord
                publicRecords={creditReport?.PulblicRecordPartition || []}
                className="mt-8"
              />
            )}
            {inquiries.length > 0 && (
              <CreditReportInquiries inquiries={inquiries} className="mt-8" />
            )}
          </>
        )}
      </div>
    </div>
  );
};
